import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useFamily } from '../../contexts/FamilyContext';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons';

export default function RecentChatsAirbnb({ limit: limitCount = 3 }) {
  const [recentChats, setRecentChats] = useState([]);
  const { familyId } = useFamily();
  const navigate = useNavigate();

  useEffect(() => {
    if (!familyId) return;

    const q = query(
      collection(db, 'conversations'),
      where('familyId', '==', familyId),
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chats = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentChats(chats);
    }, (error) => {
      // Ignore errors (might not have index yet)
      if (process.env.NODE_ENV === 'development') {
        console.log('Recent chats query error:', error);
      }
    });

    return unsubscribe;
  }, [familyId, limitCount]);

  if (recentChats.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif font-bold text-lg text-text-primary">
          Recent Chats
        </h3>
        <button
          onClick={() => navigate('/chats')}
          className="text-sm font-semibold text-primary hover:text-primary-dark flex items-center gap-1 group"
        >
          View All
          <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="space-y-3">
        {recentChats.map((chat, index) => (
          <motion.button
            key={chat.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            onClick={() => navigate(`/chats/${chat.id}`)}
            className="w-full bg-white border border-border rounded-xl p-4 hover:border-primary hover:shadow-md transition-all duration-200 text-left group"
          >
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faComments} className="text-primary text-lg mt-0.5 group-hover:scale-110 transition-transform" />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-text-primary mb-1 truncate group-hover:text-primary transition-colors">
                  {chat.title || 'New Conversation'}
                </h4>
                <p className="text-sm text-text-secondary">
                  {formatTimeAgo(chat.updatedAt)}
                </p>
              </div>
              <svg className="w-5 h-5 text-text-secondary group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}

function formatTimeAgo(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffInMs = now - date;
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 7) return `${diffInDays}d ago`;
  return date.toLocaleDateString();
}

